import Icon from "@Components/generic/icon";
import { faCaretDown, faCaretUp, faCircleDown, faCircleUp, IconDefinition } from "@fortawesome/pro-solid-svg-icons";
import useGlobalContext from "@hooks/common/general/use-global-context";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { isNaN } from "lodash-es";
import { FC } from "react";

interface IImprovementIndicator {
  previousScore?: number;
  score?: number;
  percentageChange?: number;
  metricLabel?: string;
  fontSize?: number | string;
  highlightScore?: boolean;
  reverse?: boolean;
  smallArrows?: boolean;
  fontWeight?: number;
  colors?: {
    positive: string;
    negative: string;
  };
  icons?: {
    positive: IconDefinition;
    negative: IconDefinition;
  };
  iconSizes?: {
    default: string;
    small: string;
  };
}

const ImprovementIndicator: FC<IImprovementIndicator> = ({
  previousScore,
  score,
  percentageChange,
  fontSize = "inherit",
  colors,
  highlightScore = false,
  reverse = false,
  smallArrows = false,
  icons,
  fontWeight = 700,
  iconSizes = {
    default: "0.6em",
    small: "0.9em",
  },
}) => {
  const { globalState } = useGlobalContext();
  const theme = useTheme();
  const isMobile = globalState.device.isMobile;
  const themeColors: { positive: string; negative: string } = {
    positive: colors?.positive || theme.palette.dsSuccess.v300,
    negative: colors?.negative || theme.palette.error.light,
  };
  const scoreValue = score ?? 0;
  const previousScoreValue = previousScore ?? 0;
  const isUsingPrecomputedPercentage = percentageChange !== undefined;
  const isPositive = isUsingPrecomputedPercentage ? percentageChange > 0 : scoreValue > previousScoreValue;
  let percentage = isUsingPrecomputedPercentage
    ? Math.abs(percentageChange)
    : Number((((Math.abs(previousScoreValue - scoreValue) / previousScoreValue) * 100).toFixed(0)));
  if (percentage === Infinity) percentage = 100;
  const arrows = smallArrows
    ? {
        positive: faCaretUp,
        negative: faCaretDown,
      }
    : icons || {
        positive: faCircleUp,
        negative: faCircleDown,
      };

  // If NaN use placeholder character to keep the layout consistent
  if (isNaN(percentage)) return <Box sx={{ fontSize: fontSize, opacity: 0 }}>_</Box>;

  return (
    <Stack direction={reverse ? "row-reverse" : "row"} spacing={0} sx={{ fontSize: fontSize, position: "relative" }} alignItems="baseline">
      <Box display="flex" alignItems={"baseline"}>
        <Typography
          variant={isMobile ? "textBase700" : "text2xl700"}
          color={!highlightScore ? "dsNeutral.v300" : isPositive ? themeColors.positive : themeColors.negative}
        >{`${!isPositive ? "" : ""}${percentage}`}</Typography>
        <Typography
          fontWeight={fontWeight}
          sx={{
            fontSize: "60%",
            color: !highlightScore ? "dsNeutral.v300" : isPositive ? themeColors.positive : themeColors.negative,
          }}
        >
          %
        </Typography>
      </Box>
      <Icon
        icon={isPositive ? arrows.positive : arrows.negative}
        sx={{
          color: isPositive ? themeColors.positive : themeColors.negative,
          fontSize: iconSizes.default,
          ...(smallArrows && {
            fontSize: iconSizes.small,
          }),
          ...(reverse
            ? {
                marginRight: "0.4em",
              }
            : {
                marginLeft: "0.4em",
              }),
        }}
      />
    </Stack>
  );
};

export default ImprovementIndicator;
